import { useCallback, useMemo } from 'react';
import { CONSUMPTION, ENGINE } from '../constants/characteristics';
import { getEnvironmentalAndTechData } from '@utils/Characteristics.utils';
import { useTranslations } from './useTranslations';
import { FUEL_KEYS } from '../constants/main';
import { IConfigurableCar } from '../interfaces/Car';
import { useMoneyFormatter } from './useMoneyFormatter';

export const useTechSpec = (carDetail: IConfigurableCar) => {
    const { t } = useTranslations();
    const { formatAmount } = useMoneyFormatter();

    const { engine } = carDetail;

    const { environmentalData = null } = engine?.specs ? getEnvironmentalAndTechData(engine.specs) : {};

    const legalTextUrl = useMemo(() => {
        switch (carDetail.fuel as FUEL_KEYS) {
            case FUEL_KEYS.ESSENCE:
                return t('basket.Essence.legalTextUrl');
            case FUEL_KEYS.DIESEL_ES:
            case FUEL_KEYS.DIESEL:
                return t('basket.Diesel.legalTextUrl');
            case FUEL_KEYS.HYBRIDE_ESSENCE:
            case FUEL_KEYS.PETROL_HYBRID_DE:
                return t('basket.Hybride-Essence.legalTextUrl');
            case FUEL_KEYS.ELECTRIQUE:
            case FUEL_KEYS.ELECTRIC_DE:
                return t('basket.Electrique.legalTextUrl');
            case FUEL_KEYS.ELECTRIC_ES:
            case FUEL_KEYS.ELECTRIC:
                return t('basket.Electric.legalTextUrl');
            case FUEL_KEYS.PETROL:
            case FUEL_KEYS.PETROL_ES:
                return t('basket.Petrol.legalTextUrl');
            case FUEL_KEYS.TURBO_DIESEL:
                return t('basket.Turbo Diesel.legalTextUrl');
            case FUEL_KEYS.PETROL_HYBRID_ES:
            case FUEL_KEYS.PETROL_HYBRID:
                return t('basket.Petrol-Hybrid.legalTextUrl');
            case FUEL_KEYS.PETROL_DE:
                return t('basket.Benzin.legalTextUrl');
        }
    }, [carDetail.fuel]);

    const fuelLegalText = useMemo(() => {
        switch (carDetail.fuel as FUEL_KEYS) {
            case FUEL_KEYS.ESSENCE:
                return t('technicalFeatures.environmentalDetails.text.Essence');
            case FUEL_KEYS.DIESEL:
            case FUEL_KEYS.DIESEL_ES:
                return t('technicalFeatures.environmentalDetails.text.Diesel');
            case FUEL_KEYS.HYBRIDE_ESSENCE:
            case FUEL_KEYS.PETROL_HYBRID_DE:
                return t('technicalFeatures.environmentalDetails.text.Hybride-Essence');
            case FUEL_KEYS.ELECTRIQUE:
            case FUEL_KEYS.ELECTRIC_DE:
                return t('technicalFeatures.environmentalDetails.text.Electrique');
            case FUEL_KEYS.ELECTRIC_IT:
            case FUEL_KEYS.ELECTRIC_ES:
            case FUEL_KEYS.ELECTRIC:
                return t('technicalFeatures.environmentalDetails.text.Electric');
            case FUEL_KEYS.PETROL:
            case FUEL_KEYS.PETROL_ES:
            case FUEL_KEYS.PETROL_IT:
                return t('technicalFeatures.environmentalDetails.text.Petrol');
            case FUEL_KEYS.TURBO_DIESEL:
                return t('technicalFeatures.environmentalDetails.text.Turbo Diesel');
            case FUEL_KEYS.PETROL_HYBRID_ES:
            case FUEL_KEYS.PETROL_HYBRID:
                return t('technicalFeatures.environmentalDetails.text.Petrol-Hybrid');
            case FUEL_KEYS.PLUG_IN_HYBRID:
                return t('technicalFeatures.environmentalDetails.text.Plug-in Hybrid');
            case FUEL_KEYS.DIESEL_HDI:
                return t('technicalFeatures.environmentalDetails.text.Diesel HDi');
            case FUEL_KEYS.PETROL_DE:
                return t('technicalFeatures.environmentalDetails.text.Benzin');
        }
    }, [carDetail.fuel]);

    const getConsumptionText = useCallback(
        (consumptionDataKey: string) => {
            switch (consumptionDataKey as CONSUMPTION | ENGINE) {
                case ENGINE.FUEL:
                    return t('technicalFeatures.environmentalDetails.engine.fuel');
                case ENGINE.POWER:
                    return t('technicalFeatures.environmentalDetails.engine.power');
                case CONSUMPTION.CO2:
                    return t('technicalFeatures.environmentalDetails.consumption.co2');
                case CONSUMPTION.CO2_COMBINED:
                    return t('technicalFeatures.environmentalDetails.consumption.co2Combined');
                case CONSUMPTION.CONSUMPTION_COMBINED:
                    return t('technicalFeatures.environmentalDetails.consumption.consumptionCombined');
                case CONSUMPTION.CYCLE_E_HIGH:
                    return t('technicalFeatures.environmentalDetails.consumption.cycleEhigh');
                case CONSUMPTION.CYCLE_HIGH:
                    return t('technicalFeatures.environmentalDetails.consumption.cycleHigh');
                case CONSUMPTION.CYCLE_LOW:
                    return t('technicalFeatures.environmentalDetails.consumption.cycleLow');
                case CONSUMPTION.CYCLE_MID:
                    return t('technicalFeatures.environmentalDetails.consumption.cycleMid');
                case CONSUMPTION.ELECTRIC_CONSUMPTION:
                    return t('technicalFeatures.environmentalDetails.consumption.electricConsumption');
                case CONSUMPTION.ELECTRIC_RANGE:
                    return t('technicalFeatures.environmentalDetails.consumption.electricRange');
                case CONSUMPTION.FUEL:
                    return t('technicalFeatures.environmentalDetails.consumption.fuel');
                case CONSUMPTION.MIX:
                    return t('technicalFeatures.environmentalDetails.consumption.mix');
            }

            const consumption = environmentalData[consumptionDataKey];

            return t('technicalFeatures.environmentalDetails.consumption.literPerKM', {
                value: formatAmount(consumption, true),
            });
        },
        [environmentalData]
    );

    const getConsumptionLabel = useCallback((consumptionKey: string): string => {
        switch (consumptionKey) {
            case CONSUMPTION.ELECTRIC_RANGE:
                return t('technicalFeatures.environmentalDetails.consumption.electricRange.label');
            case CONSUMPTION.ELECTRIC_CONSUMPTION:
                return t('technicalFeatures.environmentalDetails.consumption.electricConsumption.label');
            case ENGINE.POWER:
                return t('technicalFeatures.environmentalDetails.engine.power.label');
            case CONSUMPTION.MIX:
                return t('technicalFeatures.environmentalDetails.consumption.mix.label');
            case CONSUMPTION.CO2:
                return t('technicalFeatures.environmentalDetails.consumption.co2.label');
            case CONSUMPTION.CO2_COMBINED:
                return t('technicalFeatures.environmentalDetails.consumption.co2Combined.label');

            default:
                return consumptionKey;
        }
    }, []);

    return { getConsumptionLabel, getConsumptionText, legalTextUrl, fuelLegalText };
};
